import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { MatBottomSheetRef, MAT_BOTTOM_SHEET_DATA } from '@angular/material/bottom-sheet';
import { MatExpansionModule } from '@angular/material/expansion';
import { RTLDivDirectiveDirective } from '../../../directives/rtldiv-directive.directive';

@Component({
  selector: 'app-filter-bar-sheet',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, MatExpansionModule, RTLDivDirectiveDirective],
  templateUrl: './filter-bar-sheet.component.html',
  styleUrl: './filter-bar-sheet.component.scss'
})
export class FilterBarSheetComponent {
  page: string;
  currentLang: string;
  translationsObj: any;
  private rawData: any[] = [];

  sitesList: any[] = [];
  displaySitesList: any[] = [];
  siteSearch = '';

  typesList: any[] = [];
  displayTypesList: any[] = [];
  typeSearch = '';

  neighborhoodsList: any[] = [];
  displayNeighborhoodsList: any[] = [];
  neighborhoodSearch = '';

  collectionModeList: any[] = [];
  displayCollectionModeList: any[] = [];
  collectionModeSearch = '';

  eventCauseList: any[] = [];
  displayEventCauseList: any[] = [];
  eventCauseSearch = '';

  ticketTypeList: any[] = [];
  displayTicketTypeList: any[] = [];
  ticketTypeSearch = '';

  statusList: any[] = [];
  displayStatusList: any[] = [];
  statusSearch = '';

  constructor(
    private bottomSheetRef: MatBottomSheetRef<FilterBarSheetComponent>,
    @Inject(MAT_BOTTOM_SHEET_DATA) public data: any
  ) {
    this.page = data.page;
    this.currentLang = data.currentLang;
    this.translationsObj = data.translationsObj;
    this.rawData = data.rawData || [];

    // Rebuild every list from the full rawData so that ALL options are visible,
    // not just the ones that survived the last filterAllArrays() pass.
    // We preserve the selection state from whatever the parent currently has.
    if (this.rawData.length > 0) {
      this.buildListsFromRawData(data);
    } else {
      // Fallback when rawData is not yet available
      this.sitesList = JSON.parse(JSON.stringify(data.sitesList || []));
      this.typesList = JSON.parse(JSON.stringify(data.typesList || []));
      this.neighborhoodsList = JSON.parse(JSON.stringify(data.neighborhoodsList || []));
      this.collectionModeList = JSON.parse(JSON.stringify(data.collectionModeList || []));
      this.eventCauseList = JSON.parse(JSON.stringify(data.eventCauseList || []));
      this.ticketTypeList = JSON.parse(JSON.stringify(data.ticketTypeList || []));
      this.statusList = JSON.parse(JSON.stringify(data.statusList || []));
    }

    this.displaySitesList = [...this.sitesList];
    this.displayTypesList = [...this.typesList];
    this.displayNeighborhoodsList = [...this.neighborhoodsList];
    this.displayCollectionModeList = [...this.collectionModeList];
    this.displayEventCauseList = [...this.eventCauseList];
    this.displayTicketTypeList = [...this.ticketTypeList];
    this.displayStatusList = [...this.statusList];

    // Apply initial selections so counts reflect the current active filters
    this.updateCounts();
  }

  /**
   * Rebuilds every option list from the full rawData snapshot.
   * Previously selected items are flagged using the selection state from the
   * parent lists (data.sitesList etc.) so we start with the right checkboxes ticked.
   */
  private buildListsFromRawData(data: any) {
    const selSites = new Set((data.sitesList || []).filter((s: any) => s.selected).map((s: any) => s.site));
    const uniqueSites = [...new Set(this.rawData.map(i => i.site_name).filter(v => v && v.trim() !== ''))] as string[];
    this.sitesList = uniqueSites.map(site => ({
      site,
      num_of_bins_in_site: this.rawData.filter(i => i.site_name === site).length,
      selected: selSites.has(site)
    }));

    const selTypes = new Set((data.typesList || []).filter((t: any) => t.selected).map((t: any) => t.type));
    const uniqueTypes = [...new Set(this.rawData.map(i => i.bin_type_name).filter(v => v && v.trim() !== ''))] as string[];
    this.typesList = uniqueTypes.map(type => ({
      type,
      num_of_bins_in_type: this.rawData.filter(i => i.bin_type_name === type).length,
      selected: selTypes.has(type)
    }));

    const selNbh = new Set((data.neighborhoodsList || []).filter((n: any) => n.selected).map((n: any) => n.neighborhood));
    const uniqueNbh = [...new Set(this.rawData.map(i => i.Bin_location?.bin_neighbourhood).filter(v => v && v.trim() !== ''))] as string[];
    this.neighborhoodsList = uniqueNbh.map(neighborhood => ({
      neighborhood,
      num_of_bins_in_nbrh: this.rawData.filter(i => i.Bin_location?.bin_neighbourhood === neighborhood).length,
      selected: selNbh.has(neighborhood)
    }));

    const selCm = new Set((data.collectionModeList || []).filter((c: any) => c.selected).map((c: any) => c.id));
    const uniqueCm = [...new Set(this.rawData.map(i => i.Bin_Restrictions?.collection_mode).filter(v => v !== null && v !== undefined && v.toString().trim() !== ''))] as any[];
    this.collectionModeList = uniqueCm.map(id => ({
      id,
      name: id == 2
        ? this.translationsObj?.TICKET_MANAGEMENT?.INCLUDE_IN_WP
        : this.translationsObj?.MANAGE_BIN?.NOT_INCLUDE_IN_WORKPLAN,
      num_of_bins_in_collection_mode: this.rawData.filter(i => i.Bin_Restrictions?.collection_mode === id).length,
      selected: selCm.has(id)
    }));

    const selEc = new Set((data.eventCauseList || []).filter((e: any) => e.selected).map((e: any) => e.eventCauseEn));
    const uniqueEcEn = [...new Set(this.rawData.filter(i => i.eventCauseEn && i.eventCauseEn.trim() !== '').map(i => i.eventCauseEn))] as string[];
    this.eventCauseList = uniqueEcEn.map(eventCauseEn => {
      const match = this.rawData.find(i => i.eventCauseEn === eventCauseEn);
      return {
        eventCauseEn,
        eventCauseHe: match?.eventCauseHe || '',
        num_of_bins_in_event_cause: this.rawData.filter(i => i.eventCauseEn === eventCauseEn).length,
        selected: selEc.has(eventCauseEn)
      };
    });

    const selTt = new Set((data.ticketTypeList || []).filter((t: any) => t.selected).map((t: any) => t.ticket_type));
    const uniqueTt = [...new Set(this.rawData.filter(i => i.ticket_type && i.ticket_type.toString().trim() !== '').map(i => i.ticket_type))] as any[];
    this.ticketTypeList = uniqueTt.map(ticket_type => {
      const match = this.rawData.find(i => i.ticket_type === ticket_type);
      return {
        ticket_type,
        ticket_type_hebrew: match?.ticket_type_hebrew || '',
        num_of_tickets_in_ticket_type: this.rawData.filter(i => i.ticket_type === ticket_type).length,
        selected: selTt.has(ticket_type)
      };
    });

    const selSt = new Set((data.statusList || []).filter((s: any) => s.selected).map((s: any) => s.status));
    const uniqueSt = [...new Set(this.rawData.filter(i => i.status !== null && i.status !== undefined && i.status.toString().trim() !== '').map(i => i.status))] as any[];
    this.statusList = uniqueSt.map(status => {
      const match = this.rawData.find(i => i.status === status);
      return {
        status,
        status_txt: match?.status_txt || '',
        num_of_tickets_in_status: this.rawData.filter(i => i.status === status).length,
        selected: selSt.has(status)
      };
    });
  }

  get totalActiveFilters(): number {
    return (
      this.sitesList.filter(s => s.selected).length +
      this.typesList.filter(t => t.selected).length +
      this.neighborhoodsList.filter(n => n.selected).length +
      this.collectionModeList.filter(c => c.selected).length +
      this.eventCauseList.filter(e => e.selected).length +
      this.ticketTypeList.filter(t => t.selected).length +
      this.statusList.filter(s => s.selected).length
    );
  }

  getSelectedCount(list: any[]): number {
    return list.filter(item => item.selected).length;
  }

  toggleItem(list: any[], item: any, key: string) {
    const found = list.find(el => el[key] === item[key]);
    if (found) {
      found.selected = !found.selected;
      this.updateCounts();
    }
  }

  /**
   * Re-computes counts for every filter option based on the current selections.
   * Options are never removed — only their counts are updated.
   * This mirrors the desktop's cross-filter behaviour.
   */
  updateCounts() {
    if (!this.rawData || this.rawData.length === 0) return;

    const sel = {
      sites: this.sitesList.filter(s => s.selected).map(s => s.site),
      types: this.typesList.filter(t => t.selected).map(t => t.type),
      neighborhoods: this.neighborhoodsList.filter(n => n.selected).map(n => n.neighborhood),
      collectionModes: this.collectionModeList.filter(c => c.selected).map(c => c.id),
      eventCauses: this.eventCauseList.filter(e => e.selected).map(e => e.eventCauseEn),
      ticketTypes: this.ticketTypeList.filter(t => t.selected).map(t => t.ticket_type),
      statuses: this.statusList.filter(s => s.selected).map(s => s.status)
    };

    const filtered = this.rawData.filter(item =>
      (sel.sites.length === 0 || sel.sites.includes(item.site_name)) &&
      (sel.types.length === 0 || sel.types.includes(item.bin_type_name)) &&
      (sel.neighborhoods.length === 0 || sel.neighborhoods.includes(item.Bin_location?.bin_neighbourhood)) &&
      (sel.collectionModes.length === 0 || sel.collectionModes.includes(item.Bin_Restrictions?.collection_mode)) &&
      (sel.eventCauses.length === 0 || sel.eventCauses.includes(item.eventCauseEn)) &&
      (sel.ticketTypes.length === 0 || sel.ticketTypes.includes(item.ticket_type)) &&
      (sel.statuses.length === 0 || sel.statuses.includes(item.status))
    );

    this.sitesList.forEach(s =>
      s.num_of_bins_in_site = filtered.filter(i => i.site_name === s.site).length
    );
    this.typesList.forEach(t =>
      t.num_of_bins_in_type = filtered.filter(i => i.bin_type_name === t.type).length
    );
    this.neighborhoodsList.forEach(n =>
      n.num_of_bins_in_nbrh = filtered.filter(i => i.Bin_location?.bin_neighbourhood === n.neighborhood).length
    );
    this.collectionModeList.forEach(c =>
      c.num_of_bins_in_collection_mode = filtered.filter(i => i.Bin_Restrictions?.collection_mode === c.id).length
    );
    this.eventCauseList.forEach(e =>
      e.num_of_bins_in_event_cause = filtered.filter(i => i.eventCauseEn === e.eventCauseEn).length
    );
    this.ticketTypeList.forEach(t =>
      t.num_of_tickets_in_ticket_type = filtered.filter(i => i.ticket_type === t.ticket_type).length
    );
    this.statusList.forEach(s =>
      s.num_of_tickets_in_status = filtered.filter(i => i.status === s.status).length
    );
  }

  onSiteSearch(value: string) {
    this.displaySitesList = this.sitesList.filter(s =>
      s.site.toLowerCase().includes(value.toLowerCase())
    );
  }

  onTypeSearch(value: string) {
    this.displayTypesList = this.typesList.filter(t =>
      t.type.toLowerCase().includes(value.toLowerCase())
    );
  }

  onNeighborhoodSearch(value: string) {
    this.displayNeighborhoodsList = this.neighborhoodsList.filter(n =>
      n.neighborhood.toLowerCase().includes(value.toLowerCase())
    );
  }

  onCollectionModeSearch(value: string) {
    this.displayCollectionModeList = this.collectionModeList.filter(c =>
      c.name.toLowerCase().includes(value.toLowerCase())
    );
  }

  onEventCauseSearch(value: string) {
    this.displayEventCauseList = this.eventCauseList.filter(e => {
      const label = this.currentLang === 'iw' ? (e.eventCauseHe || '') : (e.eventCauseEn || '');
      return label.toLowerCase().includes(value.toLowerCase());
    });
  }

  onTicketTypeSearch(value: string) {
    this.displayTicketTypeList = this.ticketTypeList.filter(t => {
      const label = this.currentLang === 'iw' ? (t.ticket_type_hebrew || '') : (t.ticket_type || '');
      return label.toLowerCase().includes(value.toLowerCase());
    });
  }

  onStatusSearch(value: string) {
    this.displayStatusList = this.statusList.filter(s =>
      (s.status_txt || '').toLowerCase().includes(value.toLowerCase())
    );
  }

  clearAll() {
    [this.sitesList, this.typesList, this.neighborhoodsList,
      this.collectionModeList, this.eventCauseList, this.ticketTypeList,
      this.statusList].forEach(list => list.forEach(item => (item.selected = false)));

    this.displaySitesList = [...this.sitesList];
    this.displayTypesList = [...this.typesList];
    this.displayNeighborhoodsList = [...this.neighborhoodsList];
    this.displayCollectionModeList = [...this.collectionModeList];
    this.displayEventCauseList = [...this.eventCauseList];
    this.displayTicketTypeList = [...this.ticketTypeList];
    this.displayStatusList = [...this.statusList];

    this.siteSearch = '';
    this.typeSearch = '';
    this.neighborhoodSearch = '';
    this.collectionModeSearch = '';
    this.eventCauseSearch = '';
    this.ticketTypeSearch = '';
    this.statusSearch = '';

    this.updateCounts();
  }

  apply() {
    this.bottomSheetRef.dismiss({
      site: this.sitesList.filter(s => s.selected).map(s => s.site),
      type: this.typesList.filter(t => t.selected).map(t => t.type),
      neighborhood: this.neighborhoodsList.filter(n => n.selected).map(n => n.neighborhood),
      collectionMode: this.collectionModeList.filter(c => c.selected).map(c => c.id),
      eventCause: this.eventCauseList.filter(e => e.selected).map(e => e.eventCauseEn),
      ticketType: this.ticketTypeList.filter(t => t.selected).map(t => t.ticket_type),
      status: this.statusList.filter(s => s.selected).map(s => s.status),
      selectedLists: {
        sitesList: this.sitesList,
        typesList: this.typesList,
        neighborhoodsList: this.neighborhoodsList,
        collectionModeList: this.collectionModeList,
        eventCauseList: this.eventCauseList,
        ticketTypeList: this.ticketTypeList,
        statusList: this.statusList
      }
    });
  }
}
